<template>
  <div class="text-center">
    <!-- Booking customer information pop-up (v2 Item 6). Raised only for online-booking tickets;
         walk-in tickets go straight to the ticket management dialog as before. -->
    <v-dialog v-model="visibility" max-width="500" persistent scrollable>
      <v-card class="pa-0" flat rounded="lg">
        <!-- Header -->
        <v-toolbar dark color="rgb(206, 45, 45)" class="rounded-t-lg flex-grow-0">
          <v-btn icon disabled></v-btn>
          <v-spacer></v-spacer>
          <v-toolbar-title>Ticket No. {{ ticket.code }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="RESET_BOOKING_DETAIL_DIALOG()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="ma-0 py-3 px-6 text-left">
          <!-- Ticket Info -->
          <v-row v-for="row in ticketRows" :key="row.key" no-gutters class="py-1">
            <v-col cols="5" class="text-subtitle-2"
              >{{ row.en }} <span class="grey--text text--darken-1 font-weight-regular">{{ row.zh }}</span></v-col
            >
            <v-col cols="7" class="text-subtitle-2">{{ row.value }}</v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <!-- Booking window status + the customer information captured during online booking
               (v2 §6.3). Values are rendered exactly as returned by the backend, which owns
               masking of the contact number, email address and policy number. -->
          <v-row v-for="row in bookingRows" :key="row.key" no-gutters align="center" class="py-1">
            <v-col cols="5" class="text-subtitle-2"
              >{{ row.en }} <span class="grey--text text--darken-1 font-weight-regular">{{ row.zh }}</span></v-col
            >
            <v-col cols="7" :class="{ 'text-subtitle-2': !row.chip }">
              <v-chip v-if="row.chip" small dark :color="windowStatusColor(row.value)">{{ windowStatusLabel(row.value) }}</v-chip>
              <template v-else>{{ row.value }}</template>
            </v-col>
          </v-row>

          <div v-if="loading" class="py-2">
            <v-progress-circular indeterminate size="20" width="2" color="grey"></v-progress-circular>
            <span class="text-caption ml-2">Loading booking detail...</span>
          </div>
          <div v-else-if="!booking" class="text-caption py-2">Unable to load booking detail.</div>
        </v-card-text>

        <!-- Continue -> proceed with the existing pick flow, unchanged. Disabled only while there
             is nothing to read yet, so the pop-up cannot be skipped before the information is
             shown -- but a slow backend can never lock the operator out of picking the ticket. -->
        <v-card-actions class="px-6 pt-0 pb-4">
          <v-btn block color="rgb(206, 45, 45)" dark :disabled="isContinueDisabled" @click="continueToTicketManagement">Continue</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import api from '@/apis'
import bookingStatus from '@/mixins/bookingStatus'

export default {
  name: 'bookingDetailDialog',
  mixins: [bookingStatus],
  data() {
    return {
      booking: null,
      loading: false,
      // Monotonic token identifying the latest fetch. Guarding on this (rather than booking_id)
      // makes the stale-response check correct even when two queued tickets share a booking_id.
      reqId: 0
    }
  },
  computed: {
    ...mapState(['general', 'bookingDetailDialog']),
    visibility: {
      get() {
        return this.bookingDetailDialog.visibility
      },
      set(value) {
        if (!value) this.RESET_BOOKING_DETAIL_DIALOG()
      }
    },
    ticket() {
      return this.bookingDetailDialog.ticket
    },
    // Prefer the status persisted on the ticket at creation time; the freshly-fetched booking
    // recomputes the window against "now" and would mislabel an in-window booking ticket that
    // has since waited past its window. Fall back to the fetched booking only if absent.
    windowStatus() {
      return this.ticket.booking_window_status || (this.booking && this.booking.booking_window_status) || 'not_booking'
    },
    ticketRows() {
      return [
        { key: 'service_type', en: 'Service Type', zh: '服務類別', value: this.ticket.service_type_name || 'N/A' },
        { key: 'arrival_time', en: 'Arrival Time', zh: '到達時間', value: this.formatDateTime(this.ticket.create_datetime, 'YYYY-MM-DD HH:mm:ss') }
      ]
    },
    // Block Continue only while there is nothing to show yet. Once the seeded (or fetched)
    // booking is on screen the operator may proceed even if a refresh is still in flight.
    isContinueDisabled() {
      return this.loading && !this.booking
    },
    // v2 §6.3 customer information fields.
    bookingRows() {
      const rows = [{ key: 'booking_status', en: 'Booking', zh: '預約狀態', chip: true, value: this.windowStatus }]
      const booking = this.booking
      if (booking) {
        rows.push(
          { key: 'enquiry_option', en: 'Enquiry Option', zh: '查詢選項', value: booking.enquiry_option || 'N/A' },
          { key: 'customer_name', en: 'Customer Name', zh: '客戶姓名', value: booking.lastname || 'N/A' },
          { key: 'contact_number', en: 'Contact Number', zh: '聯絡電話', value: this.bookingContactNumber(booking) },
          { key: 'email', en: 'Email Address', zh: '電郵地址', value: booking.email || 'N/A' },
          { key: 'policy_number', en: 'Policy Number', zh: '保單號碼', value: booking.policy_number || 'N/A' },
          { key: 'booking_date', en: 'Booking Date', zh: '預約日期', value: booking.date || 'N/A' },
          { key: 'booked_timeslot', en: 'Booked Timeslot', zh: '預約時段', value: this.bookingTimeslot(booking) },
          { key: 'dedicated_staff', en: 'Dedicated Staff', zh: '指定職員', value: this.getStaffNameByID(booking.dedicated_staff_id) },
          { key: 'decision', en: 'Decision', zh: '出飛結果', value: this.ticketDecisionLabel(booking.ticket_decision || booking.effective_ticket_decision) }
        )
      }
      return rows
    }
  },
  watch: {
    visibility(open) {
      if (open) this.loadBooking()
      else this.resetDetail()
    }
  },
  // The dialog is mounted by the Home view, but its visibility lives in the store. Clear it on
  // teardown (route change, session expiry) so remounting Home cannot restore a stale pop-up whose
  // watcher would never fire again to reload the booking.
  beforeDestroy() {
    this.resetDetail()
    this.RESET_BOOKING_DETAIL_DIALOG()
  },
  methods: {
    ...mapMutations(['SET_SNACKBAR', 'INIT_TICKET_MANAGEMENT_DIALOG', 'RESET_BOOKING_DETAIL_DIALOG']),
    loadBooking() {
      // Seed from the booking object the backend attaches to the queue payload so the pop-up is
      // not blank while the authoritative record loads; the fetch below refreshes it (the nested
      // object is a creation-time snapshot, so window status and masking may have moved on).
      this.booking = this.ticket.booking || null
      this.fetchBooking(this.getTicketBookingID(this.ticket))
    },
    async fetchBooking(booking_id) {
      const reqId = ++this.reqId
      this.loading = true
      try {
        const response = await api.getBookingByID(booking_id)
        // Ignore a stale response if the operator already closed or switched the dialog while
        // this was in flight, so the pop-up never briefly shows another customer's detail.
        if (this.reqId !== reqId) return
        if (response.data.success && response.data.data && response.data.data.length) {
          this.booking = response.data.data[0]
        }
      } catch (error) {
        // Keep whatever the queue payload already gave us rather than blanking the pop-up.
        if (this.reqId === reqId)
          this.SET_SNACKBAR({ status: true, color: 'error', text: 'Cannot load the latest booking detail, showing the information attached to the ticket.', icon: 'close', timeout: '2000' })
      } finally {
        if (this.reqId === reqId) this.loading = false
      }
    },
    resetDetail() {
      this.booking = null
      this.loading = false
      // Invalidate any in-flight fetch so its late response can't populate the closed pop-up.
      this.reqId++
    },
    // After the operator has reviewed the booking information, proceed with the existing pick flow.
    // Idempotent: the button stays clickable during the dialog's leave transition, and a second
    // click must not hand an already-cleared (empty) ticket to the ticket management dialog.
    continueToTicketManagement() {
      const ticket = this.ticket
      if (!ticket || !Object.keys(ticket).length) return
      this.RESET_BOOKING_DETAIL_DIALOG()
      this.INIT_TICKET_MANAGEMENT_DIALOG({ visibility: true, ticket: ticket })
    }
  }
}
</script>
